if (!customElements.get('product-card-scroll-snap')) {
  customElements.define(
    'product-card-scroll-snap',
    class ProductCardScrollSnap extends HTMLElement {
      connectedCallback() {
        this.wrapper =
          this.querySelector('[data-scroll-snap-wrapper]') || this.querySelector('.scroll-snap-wrapper');
        if (!this.wrapper) return;

        this.slides = Array.from(this.wrapper.querySelectorAll('.scroll-snap-slide'));
        if (!this.slides.length) return;

        this.prevBtn = this.querySelector('[data-scroll-snap-prev]');
        this.nextBtn = this.querySelector('[data-scroll-snap-next]');

        const computedStyles = window.getComputedStyle(this.wrapper);
        this.direction = computedStyles.direction || 'ltr';

        this.handleScroll = this.handleScroll.bind(this);
        this.handlePrev = this.handlePrev.bind(this);
        this.handleNext = this.handleNext.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handlePointerDown = this.handlePointerDown.bind(this);

        this.wrapper.addEventListener('scroll', this.handleScroll, { passive: true });
        this.wrapper.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
        window.addEventListener('resize', this.handleResize);

        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', this.handlePrev);
        }
        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', this.handleNext);
        }

        this.dots = this.querySelectorAll('.product-card__dot');

        this.handleVideoPlay = this.handleVideoPlay.bind(this);
        this.initVideoButtons();

        this.userInteracted = false;
        this.loadSlide(0);
        this.updateButtons(0);
      }

      disconnectedCallback() {
        if (this.wrapper) {
          this.wrapper.removeEventListener('scroll', this.handleScroll);
          this.wrapper.removeEventListener('pointerdown', this.handlePointerDown);
        }
        window.removeEventListener('resize', this.handleResize);

        if (this.prevBtn) {
          this.prevBtn.removeEventListener('click', this.handlePrev);
        }
        if (this.nextBtn) {
          this.nextBtn.removeEventListener('click', this.handleNext);
        }

        this.removeVideoButtons();

        if (this.rafId) {
          cancelAnimationFrame(this.rafId);
        }
      }

      loadSlide(index) {
        const slide = this.slides[index];
        if (!slide || slide.dataset.lazyLoaded === 'true') return;

        const template = slide.querySelector('template[data-product-card-image-template]');
        if (!template) {
          slide.dataset.lazyLoaded = 'true';
          return;
        }

        const fragment = document.importNode ? document.importNode(template.content, true) : template.content.cloneNode(true);

        if (template.parentNode === slide) {
          slide.insertBefore(fragment, template);
          template.remove();
        } else {
          slide.appendChild(fragment);
        }

        slide.dataset.lazyLoaded = 'true';
      }

      loadAdjacent(index) {
        this.loadSlide(index);
        this.loadSlide(index + 1);
        this.loadSlide(index - 1);
      }

      handleScroll() {
        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.rafId = requestAnimationFrame(() => {
          this.rafId = null;
          const scrollLeft = this.wrapper.scrollLeft;
          if (!this.userInteracted && Math.abs(scrollLeft) > 1) {
            this.userInteracted = true;
          }
          const index = this.getCurrentIndex();
          this.loadAdjacent(index);
          this.updateButtons(index);
        });
      }

      handlePrev(event) {
        event.preventDefault();
        this.userInteracted = true;
        const targetIndex = Math.max(this.getCurrentIndex() - 1, 0);
        this.scrollToIndex(targetIndex);
      }

      handleNext(event) {
        event.preventDefault();
        this.userInteracted = true;
        const targetIndex = Math.min(this.getCurrentIndex() + 1, this.slides.length - 1);
        this.scrollToIndex(targetIndex);
      }

      handleResize() {
        const index = this.getCurrentIndex();
        this.loadAdjacent(index);
        this.updateButtons(index);
      }

      handlePointerDown() {
        this.userInteracted = true;
        const index = this.getCurrentIndex();
        this.loadSlide(index);
        this.loadSlide(index + 1);
        this.loadSlide(index - 1);
      }

      scrollToIndex(index) {
        const slide = this.slides[index];
        if (!slide || !this.wrapper) return;

        this.userInteracted = true;
        this.loadSlide(index);
        this.loadSlide(index + 1);
        this.loadSlide(index - 1);

        slide.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });

        this.updateButtons(index);
      }

      getCurrentIndex() {
        if (!this.wrapper || !this.slides.length) return 0;

        const wrapperRect = this.wrapper.getBoundingClientRect();
        const wrapperCenter = wrapperRect.left + wrapperRect.width / 2;
        let closestIndex = 0;
        let minDifference = Number.POSITIVE_INFINITY;

        this.slides.forEach((slide, index) => {
          const rect = slide.getBoundingClientRect();
          const slideCenter = rect.left + rect.width / 2;
          const difference = Math.abs(slideCenter - wrapperCenter);
          if (difference < minDifference) {
            minDifference = difference;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      updateButtons(index = this.getCurrentIndex()) {
        if (this.prevBtn) {
          this.prevBtn.toggleAttribute('disabled', index <= 0);
        }
        if (this.nextBtn) {
          this.nextBtn.toggleAttribute('disabled', index >= this.slides.length - 1);
        }
        if (this.dots) {
          this.dots.forEach((dot, i) => {
            dot.classList.toggle('is-active', i === index);
          });
        }
      }

      initVideoButtons() {
        this.videoButtons = this.querySelectorAll('[data-video-play-button]');
        this.videoButtons.forEach((button) => {
          button.addEventListener('click', this.handleVideoPlay, true);
        });
      }

      removeVideoButtons() {
        if (this.videoButtons) {
          this.videoButtons.forEach((button) => {
            button.removeEventListener('click', this.handleVideoPlay);
          });
        }
      }

      handleVideoPlay(event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        const button = event.currentTarget;
        const slide = button.closest('.product-card__video-slide');
        if (!slide) return;
        
        const link = slide.closest('a');
        if (link) {
          link.style.pointerEvents = 'none';
          setTimeout(() => {
            link.style.pointerEvents = '';
          }, 100);
        }

        const template = slide.querySelector('template[data-product-card-video-template]');
        if (!template) return;

        const poster = slide.querySelector('.product-card__video-poster');
        if (!poster) return;

        const fragment = document.importNode ? document.importNode(template.content, true) : template.content.cloneNode(true);

        poster.remove();

        const videoElement = fragment.querySelector('video-element');
        if (videoElement) {
          slide.appendChild(fragment);
          
          const video = videoElement.querySelector('video');
          if (video) {
            video.play().catch(() => {});
          }
        } else {
          slide.appendChild(fragment);
        }

        slide.dataset.videoLoaded = 'true';
      }
    }
  );
}
